import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from '../../firebase/app';
import { BrandScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { SiroccoLogo } from '../../theme/illustrations';
import { t } from '../../i18n';

/**
 * Ce qui se passe quand on suit le lien d'un courriel.
 *
 * # Pourquoi cette page existe
 *
 * Firebase en fournit une : c'est elle qui affichait « Password changed »
 * en anglais, sur fond blanc, a une adresse ou se lisait le nom de code
 * des debuts du projet. Un membre qui vient de confirmer son adresse ou
 * de choisir un mot de passe est encore en train de se faire une idee de
 * l'application — il n'a pas a atterrir chez quelqu'un d'autre.
 *
 * Le code a usage unique voyage dans l'adresse (`oobCode`). Tout ce que
 * la page de Firebase faisait, le SDK sait le faire : verifier le code,
 * l'appliquer, changer le mot de passe. Il ne restait qu'a l'habiller.
 *
 * # Les trois cas
 *
 * `verifyEmail` confirme une adresse a l'inscription. `resetPassword`
 * demande un nouveau mot de passe. `recoverEmail` annule un changement
 * d'adresse — Firebase l'envoie de lui-meme, et il vaut mieux le traiter
 * que le laisser tomber sur une page vide.
 */

type Etat = 'lecture' | 'formulaire' | 'fait' | 'erreur';

export function ActionScreen() {
  const [parametres] = useSearchParams();
  const navigate = useNavigate();

  const mode = parametres.get('mode') ?? '';
  const code = parametres.get('oobCode') ?? '';

  const [etat, setEtat] = useState<Etat>('lecture');
  const [adresse, setAdresse] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [motDePasse, setMotDePasse] = useState('');
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    if (!code) {
      setErreur(t.auth.action.incomplet);
      setEtat('erreur');
      return;
    }

    /*
      Un code se verifie avant de servir : expire, deja utilise, ou
      simplement faux. Mieux vaut le dire tout de suite que de laisser
      quelqu'un choisir un mot de passe pour rien.
    */
    if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth(), code)
        .then((email) => {
          setAdresse(email);
          setEtat('formulaire');
        })
        .catch(() => {
          setErreur(t.auth.action.expireNouveau);
          setEtat('erreur');
        });
      return;
    }

    applyActionCode(auth(), code)
      .then(() => setEtat('fait'))
      .catch(() => {
        setErreur(t.auth.action.expire);
        setEtat('erreur');
      });
  }, [code, mode]);

  async function choisir(evenement: FormEvent) {
    evenement.preventDefault();
    if (motDePasse.length < 6) {
      setErreur(t.auth.action.sixCaracteres);
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      await confirmPasswordReset(auth(), code, motDePasse);
      setEtat('fait');
    } catch {
      setErreur(t.auth.action.pasChange);
    } finally {
      setEnvoi(false);
    }
  }

  const titre =
    mode === 'resetPassword'
      ? etat === 'fait'
        ? t.auth.action.titreChange
        : t.auth.action.titreNouveau
      : mode === 'recoverEmail'
        ? t.auth.action.titreRetablie
        : t.auth.action.titreConfirmee;

  return (
    <BrandScreen>
      {/*
        Le gabarit de la connexion, et non celui de l'inscription : la page
        tient en trois lignes, et c'est vers la connexion qu'elle ramene.
      */}
      <div className="auth">
        <div className="auth__logo">
          <SiroccoLogo markSize={50} />
        </div>

        <h1 className="auth__title">{etat === 'lecture' ? t.auth.action.unInstant : titre}</h1>

        {etat === 'erreur' && <p className="auth__error">{erreur}</p>}

        {etat === 'formulaire' && (
          <form className="auth__form" onSubmit={choisir}>
            <p className="auth__switch auth__switch--top">
              {t.auth.action.pour.avant} <strong>{adresse}</strong>
              {t.auth.action.pour.apres}
            </p>
            <div className="auth__block">
              <Field
                label={t.auth.action.nouveau}
                type="password"
                value={motDePasse}
                autoComplete="new-password"
                onChange={(evenement) => setMotDePasse(evenement.target.value)}
              />
            </div>
            {erreur && <p className="auth__error">{erreur}</p>}
            {/*
              `brand` et non la couleur du spot : on ne sait pas encore qui
              lit, et cet ecran reste turquoise comme le reste de la marque.
            */}
            <Button
              type="submit"
              variant="brand"
              block
              loading={envoi}
              className="auth__submit"
            >
              {t.auth.action.changer}
            </Button>
          </form>
        )}

        {etat === 'fait' && (
          <>
            <p className="auth__switch auth__switch--top">
              {mode === 'resetPassword'
                ? t.auth.action.connecteToi
                : t.auth.action.bienvenue}
            </p>
            {/*
              Le retour a l'application installee d'abord, le site ensuite.
              iOS ne dit jamais s'il a su ouvrir le schema : le second
              bouton reste donc la main.
            */}
            <a className="auth__bouton-lien" href="sirocco://">
              {t.auth.action.ouvrir}
            </a>
            <button type="button" className="auth__link" onClick={() => navigate('/login')}>
              {t.auth.action.continuerSite}
            </button>
          </>
        )}
      </div>
    </BrandScreen>
  );
}
